import { parseFieldList } from "@repo/shared";
import {
  type EninLookupResult,
  type FinancialFieldDefinition,
  type FinancialSituationInput,
  type FinancialSituationModelSettings,
  type FinancialSituationStatusDef,
  type FsClientType,
  fieldAppliesTo,
  type MissingRequirementReason,
  resolveEninCompanyId,
} from "@repo/shared/types";
import { useCallback, useMemo } from "react";
import {
  DEFAULT_ASSET_FIELDS,
  DEFAULT_COMMITMENT_FIELDS,
  DEFAULT_INCOME_FIELDS,
  DEFAULT_LIABILITY_FIELDS,
} from "@/lib/constants/financial-situation-defaults";
import {
  useEninLookupFlowInstanceValue,
  useSearchAndSaveEninLookup,
} from "@/lib/hooks/use-flow-instances/use-enin-lookup";
import {
  useFinancialSituationFlowInstanceValue,
  useSaveFinancialSituation,
} from "@/lib/hooks/use-flow-instances/use-financial-situation";
import { useThirdPartyFeatures } from "@/lib/hooks/use-third-party-features";
import { extractFinancials } from "../enin-lookup/enin-extract";
import {
  type EninPrefill,
  FinancialSituationWidget,
} from "../financial-situation-widget/financial-situation-widget";

export type FinancialSituationConfig = {
  style?: "none" | "border" | "box";
  title?: string;
  description?: string;
  incomeFields?: any[];
  assetFields?: any[];
  liabilityFields?: any[];
  commitmentFields?: any[];
  showIncome?: boolean;
  showAssets?: boolean;
  showLiabilities?: boolean;
  showCommitments?: boolean;
  showCapacity?: boolean;
  showStabilityQuestion?: boolean;
  showExpectingChangesQuestion?: boolean;
  showCapacityClassification?: boolean;
  showSummaryMetrics?: boolean;
};

export type WizardFinancialSituationModel = {
  id: string;
  incomeFields: string;
  assetFields: string;
  liabilityFields: string;
  commitmentFields: string;
  statuses?: FinancialSituationStatusDef[] | null;
  /** QP-176/QP-183 — model business-rule settings. */
  settings?: FinancialSituationModelSettings | null;
  currency: string;
};

/**
 * QP-176/QL-140 — whether the model has at least one field applicable to this
 * client type. Mirrors the engine's `fsHasApplicableFieldsByType` (loader) so
 * the wizard shows the widget vs the N/A message consistently with what
 * completion counts. A missing model has no fields of any type.
 */
export function modelHasFieldsFor(
  model: WizardFinancialSituationModel | null | undefined,
  clientType: FsClientType,
): boolean {
  if (!model) return false;
  for (const column of [
    model.incomeFields,
    model.assetFields,
    model.liabilityFields,
    model.commitmentFields,
  ]) {
    try {
      const fields = JSON.parse(column || "[]") as FinancialFieldDefinition[];
      if (fields.some((f) => fieldAppliesTo(f, clientType))) return true;
    } catch {
      // Malformed list — no fields of this type (matches the engine's conservatism).
    }
  }
  return false;
}

type WizardFinancialSituationProps = {
  instanceId: string;
  disabled: boolean;
  config: FinancialSituationConfig;
  financialSituationModel?: WizardFinancialSituationModel | null;
  /**
   * QP-176/QL-140 — the RAW client type; filters the model's field lists per
   * `fieldAppliesTo` and selects the capacity variant.
   */
  clientType: FsClientType;
  /** QP-176 — enable the standalone ENIN fetch for Company clients. */
  clientOrgId?: string | null;
  clientCountry?: string | null;
  /** QP-184 — Group clients: member count scales the capacity buffer floor. */
  capacityMemberCount?: number;
  /** QL-107 — client custom field values by field id; drives the fixed-buffer trigger. */
  clientCustomValues?: Record<string, string>;
  /** QP-183 — the FS missing[] reason; drives the deposit-gate banner. */
  missingReason?: MissingRequirementReason;
  flowCurrency: string;
  language: string;
  hasValidationError?: boolean;
};

export function WizardFinancialSituation({
  instanceId,
  disabled,
  config,
  financialSituationModel,
  clientType,
  clientOrgId,
  clientCountry,
  capacityMemberCount,
  clientCustomValues,
  missingReason,
  flowCurrency,
  language,
  hasValidationError,
}: WizardFinancialSituationProps) {
  const {
    inputData,
    calculatedData,
    isLoading: valueLoading,
  } = useFinancialSituationFlowInstanceValue(instanceId);
  const saveFinancialSituation = useSaveFinancialSituation(instanceId);

  // QP-176 — standalone ENIN fetch for the FS component (Company sessions).
  // The button is offered when the tenant's ENIN feature is enabled and the
  // client's orgId/country resolve to a supported ENIN company id; clicking
  // runs the lookup on demand (persisting the same enin_lookup value the
  // Company Lookup component uses) and returns the P&L figures. A previously
  // persisted lookup is reused without a new ENIN call.
  // 3rd-party toggle (settings → third-party). Optimistic while loading —
  // the server enforces the gate regardless (requireFeature → 403).
  const { features: thirdPartyFeatures } = useThirdPartyFeatures();
  const eninFeatureEnabled = thirdPartyFeatures?.eninEnabled !== false;
  const { calculatedData: eninCalculatedData } = useEninLookupFlowInstanceValue(
    clientType === "Company" ? instanceId : null,
  );
  const runEninLookup = useSearchAndSaveEninLookup(instanceId);

  const extractPrefill = useCallback(
    (calculated: string | null | undefined): EninPrefill | null => {
      if (!calculated) return null;
      try {
        const result = JSON.parse(calculated) as EninLookupResult;
        if (!result?.hasBeenChecked || !result.bundle) return null;
        const financials = extractFinancials(result.bundle.accounting_highlights);
        if (
          !financials ||
          (financials.revenue === null &&
            financials.netIncome === null &&
            financials.cashAndDeposits === null)
        ) {
          return null;
        }
        return {
          year: financials.year,
          revenue: financials.revenue,
          netIncome: financials.netIncome,
          cashAndDeposits: financials.cashAndDeposits,
        };
      } catch {
        return null;
      }
    },
    [],
  );

  const eninPrefill = useMemo(
    () => (clientType === "Company" ? extractPrefill(eninCalculatedData) : null),
    [clientType, eninCalculatedData, extractPrefill],
  );

  const eninCompanyId =
    clientType === "Company" && clientOrgId && clientCountry
      ? resolveEninCompanyId(clientOrgId, clientCountry)
      : null;
  const eninAvailable = eninFeatureEnabled && eninCompanyId !== null;

  const fetchEninFinancials = useCallback(async (): Promise<EninPrefill | null> => {
    if (eninPrefill) return eninPrefill;
    if (!eninCompanyId) return null;
    const result = await runEninLookup.mutateAsync({
      orgId: clientOrgId ?? undefined,
      country: clientCountry ?? undefined,
    });
    if (!result.success) return null;
    return extractPrefill(result.calculatedData);
  }, [eninPrefill, eninCompanyId, clientOrgId, clientCountry, runEninLookup, extractPrefill]);

  // Resolve fields: model fields > component config fields > defaults
  const parseModelFields = (json: string | undefined) => {
    // QL-586 — sanitised like every API reader (parseFieldList never throws).
    const parsed = parseFieldList(json);
    return parsed.length > 0 ? parsed : undefined;
  };

  // QP-176 — filter each list to the fields applicable to this client type.
  // Config/default fallbacks carry no clientTypes → Person-only per
  // `fieldAppliesTo`, preserving the pre-QP-176 Person behavior exactly.
  const filterByClientType = (fields: FinancialFieldDefinition[]) =>
    fields.filter((f) => fieldAppliesTo(f, clientType));

  const resolvedIncomeFields = filterByClientType(
    parseModelFields(financialSituationModel?.incomeFields) ||
      (config.incomeFields?.length ? config.incomeFields : DEFAULT_INCOME_FIELDS),
  );
  const resolvedAssetFields = filterByClientType(
    parseModelFields(financialSituationModel?.assetFields) ||
      (config.assetFields?.length ? config.assetFields : DEFAULT_ASSET_FIELDS),
  );
  const resolvedLiabilityFields = filterByClientType(
    parseModelFields(financialSituationModel?.liabilityFields) ||
      (config.liabilityFields?.length ? config.liabilityFields : DEFAULT_LIABILITY_FIELDS),
  );
  const resolvedCommitmentFields = filterByClientType(
    parseModelFields(financialSituationModel?.commitmentFields) ||
      (config.commitmentFields?.length ? config.commitmentFields : DEFAULT_COMMITMENT_FIELDS),
  );

  const handleChange = (input: FinancialSituationInput) => {
    saveFinancialSituation.mutate(input);
  };

  if (valueLoading) return null;

  return (
    <FinancialSituationWidget
      flowInstanceId={instanceId}
      inputData={inputData}
      calculatedData={calculatedData}
      statusDefs={financialSituationModel?.statuses ?? []}
      settings={financialSituationModel?.settings ?? null}
      clientType={clientType}
      eninPrefill={eninPrefill}
      eninAvailable={eninAvailable}
      eninFetching={runEninLookup.isPending}
      onFetchEninFinancials={fetchEninFinancials}
      capacityMemberCount={capacityMemberCount}
      clientCustomValues={clientCustomValues}
      missingReason={missingReason}
      onChange={handleChange}
      disabled={disabled}
      incomeFields={resolvedIncomeFields}
      assetFields={resolvedAssetFields}
      liabilityFields={resolvedLiabilityFields}
      commitmentFields={resolvedCommitmentFields}
      showIncome={config.showIncome !== false && resolvedIncomeFields.length > 0}
      showAssets={config.showAssets !== false && resolvedAssetFields.length > 0}
      showLiabilities={config.showLiabilities !== false && resolvedLiabilityFields.length > 0}
      showCommitments={config.showCommitments !== false && resolvedCommitmentFields.length > 0}
      showCapacity={config.showCapacity !== false}
      showStabilityQuestion={config.showStabilityQuestion !== false}
      showExpectingChangesQuestion={config.showExpectingChangesQuestion !== false}
      showCapacityClassification={config.showCapacityClassification !== false}
      showSummaryMetrics={config.showSummaryMetrics !== false}
      currency={flowCurrency}
      style={config.style}
      title={config.title}
      description={config.description}
      language={language}
      hasValidationError={hasValidationError}
    />
  );
}
